/**
 * RN Evaluations Tab Component for HR Payroll Page
 * Administrator-only evaluations for Registered Nurses
 */
import { Button } from "@/components/ui/button";
import { FileText, Plus, CheckCircle, Trash2, Stethoscope } from "lucide-react";

export function RNEvaluationsTab({
  rnEvaluations,
  rnDuties,
  onAddClick,
  onExportPdf,
  onComplete,
  onDelete,
  getGradeColor,
  getGradeLabel
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="rn-evaluations-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">RN/Supervising Nurse Evaluations</h2>
          <p className="text-sm text-slate-500">Administrator-only evaluations for Registered Nurses</p>
        </div>
        <Button onClick={onAddClick} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-rn-eval-btn">
          <Plus className="w-4 h-4" />
          New RN Evaluation
        </Button>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> RN evaluations are visible only to Administrators. These evaluate supervising nurse 
          performance in areas including staff management, clinical oversight, and agency operations.
        </p>
      </div>

      {rnEvaluations.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Stethoscope className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>No RN evaluations yet.</p>
          <p className="text-sm">Click &quot;New RN Evaluation&quot; to create the first one.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rnEvaluations.map((eval_) => (
            <div key={eval_.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">{eval_.employee_name}</h3>
                  <p className="text-sm text-slate-500">
                    Evaluated by {eval_.evaluator_name} on {new Date(eval_.evaluation_date).toLocaleDateString()}
                  </p>
                  {eval_.period_start && eval_.period_end && (
                    <p className="text-xs text-slate-400">
                      Period: {eval_.period_start} to {eval_.period_end}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(Math.round(eval_.average_rating))}`}>
                    Avg: {eval_.average_rating?.toFixed(1)} ({getGradeLabel(Math.round(eval_.average_rating))})
                  </span>
                  <p className={`text-xs mt-1 ${eval_.status === 'completed' ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {eval_.status === 'completed' ? '✓ Completed' : '○ Draft'}
                  </p>
                </div>
              </div>

              {/* Duty Ratings Summary - First 7 */}
              <div className="mt-4 grid grid-cols-7 gap-2">
                {rnDuties.slice(0, 7).map((duty) => (
                  <div key={duty.id} className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500 truncate" title={duty.description}>#{duty.number}</p>
                    <span className={`text-sm font-medium ${getGradeColor(eval_[duty.id])}`}>
                      {eval_[duty.id]}
                    </span>
                  </div>
                ))}
              </div>
              {/* Duty Ratings Summary - Last 6 */}
              <div className="mt-2 grid grid-cols-6 gap-2">
                {rnDuties.slice(7, 13).map((duty) => (
                  <div key={duty.id} className="text-center p-2 bg-white rounded border">
                    <p className="text-xs text-slate-500 truncate" title={duty.description}>#{duty.number}</p>
                    <span className={`text-sm font-medium ${getGradeColor(eval_[duty.id])}`}>
                      {eval_[duty.id]}
                    </span>
                  </div>
                ))}
              </div>

              {/* Goals */}
              {eval_.goals && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">Goals</h4>
                  <p className="text-sm text-blue-700">{eval_.goals}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-3 items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onExportPdf(eval_.id)}
                  className="flex items-center gap-2"
                  data-testid={`export-rn-pdf-btn-${eval_.id}`}
                >
                  <FileText className="w-4 h-4" />
                  Export PDF
                </Button>
                {eval_.status !== 'completed' && (
                  <Button
                    size="sm"
                    onClick={() => onComplete(eval_.id, eval_.employee_name)}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`complete-rn-btn-${eval_.id}`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark Complete
                  </Button>
                )}
                {eval_.status === 'completed' && (
                  <span className="text-sm text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Completed
                    {eval_.completed_at && (
                      <span className="text-slate-500 ml-1">
                        on {new Date(eval_.completed_at).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(eval_.id, eval_.employee_name)}
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                  data-testid={`delete-rn-btn-${eval_.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
